import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import {
  VOUCHER_MAX_QUANTITY,
  VOUCHER_MAX_USES,
  VOUCHER_MIN_USES,
  VoucherCurrency,
  VoucherMessage,
  explainVoucher,
  formatAmount,
  startOfToday,
  validateVoucherRules,
} from './voucher.util';

/**
 * The rule fields of a generated voucher: how many, how much, in what currency,
 * until when, and how it may be spent.
 *
 * One component for both places a voucher is defined (the Vouchers page and the
 * "Generate vouchers" prize mode in the contest form), so the limits, the
 * conditional fields and the plain-language explanation are identical in both.
 *
 * The parent owns the FormGroup. Control names are fixed:
 *   quantity, voucher_value, voucher_currency, voucher_expires_at,
 *   usage_type ('SINGLE_USE' | 'MULTI_USE'), carry_over_balance, max_uses
 *
 * Locking never disables the controls (a disabled control drops out of
 * `form.value`, which the prize save reads); it only stops the inputs reacting.
 */
@Component({
  selector: 'app-voucher-fields',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzSelectModule,
    NzDatePickerModule,
    NzSwitchModule,
    NzToolTipModule,
    TranslateModule,
  ],
  templateUrl: './voucher-fields.component.html',
  styleUrls: ['./voucher-fields.component.scss'],
})
export class VoucherFieldsComponent {
  @Input({ required: true }) form!: FormGroup;
  @Input() currencies: VoucherCurrency[] = [];

  /** Hide the quantity box where the count is fixed (a single voucher). */
  @Input() showQuantity = true;
  /** i18n key for the quantity label; the prize mode calls it "winners". */
  @Input() quantityLabelKey = 'vouchers.fields.quantity.label';
  @Input() quantityHintKey = 'vouchers.fields.quantity.hint';

  /** Value, currency, expiry and usage rules can no longer change (vouchers were handed out). */
  @Input() rulesLocked = false;
  /** The quantity cannot go below this many (vouchers already handed out). */
  @Input() minQuantity = 1;
  /** Show every problem, not only those on fields the user has touched. */
  @Input() showAllErrors = false;

  readonly maxQuantity = VOUCHER_MAX_QUANTITY;
  readonly minUses = VOUCHER_MIN_USES;
  readonly maxUses = VOUCHER_MAX_USES;
  readonly minDate = startOfToday();

  private get raw(): any {
    return this.form?.getRawValue?.() ?? {};
  }

  get isMulti(): boolean {
    return this.raw.usage_type === 'MULTI_USE';
  }

  get carry(): boolean {
    return this.raw.carry_over_balance === true;
  }

  /** The selected currency, for the exchange-rate helper. */
  get selectedCurrency(): VoucherCurrency | undefined {
    return this.currencies.find((c) => c.code === this.raw.voucher_currency);
  }

  /** "1 USD = 3.6725 AED", or nothing when the currency is USD or unknown. */
  get rateHint(): VoucherMessage | null {
    const c = this.selectedCurrency;
    if (!c || c.code === 'USD' || !(c.usd_rate > 0)) return null;
    const value = Number(this.raw.voucher_value);
    return {
      key: value > 0 ? 'vouchers.fields.currency.rateWithValue' : 'vouchers.fields.currency.rate',
      params: {
        rate: formatAmount(c.usd_rate),
        currency: c.code,
        usd: formatAmount(value / c.usd_rate),
        value: formatAmount(value),
      },
    };
  }

  get explanation(): VoucherMessage {
    const r = this.raw;
    return explainVoucher({
      value: r.voucher_value === '' ? null : Number(r.voucher_value),
      currency: r.voucher_currency,
      usageType: r.usage_type,
      carry: r.carry_over_balance === true,
      maxUses: r.max_uses === '' || r.max_uses === undefined ? null : r.max_uses,
    });
  }

  /** Problems keyed by the control they belong to. */
  get problems(): Record<string, string> {
    const r = this.raw;
    const keys = validateVoucherRules(
      {
        quantity: r.quantity === '' || r.quantity === undefined ? null : Number(r.quantity),
        value: r.voucher_value === '' || r.voucher_value === undefined ? null : Number(r.voucher_value),
        currency: r.voucher_currency,
        expires: r.voucher_expires_at,
        usageType: r.usage_type,
        carry: r.carry_over_balance === true,
        maxUses: r.max_uses === '' || r.max_uses === undefined ? null : r.max_uses,
      },
      // Once vouchers are out, the rules cannot change, so an expiry already in the
      // past (the day has simply come) is not something the user can fix here.
      { quantityRequired: this.showQuantity, checkExpiry: !this.rulesLocked },
    );

    const map: Record<string, string> = {};
    const field: Record<string, string> = {
      'vouchers.errors.quantity': 'quantity',
      'vouchers.errors.value': 'voucher_value',
      'vouchers.errors.currency': 'voucher_currency',
      'vouchers.errors.expiryRequired': 'voucher_expires_at',
      'vouchers.errors.expiryFuture': 'voucher_expires_at',
      'vouchers.errors.expiryFar': 'voucher_expires_at',
      'vouchers.errors.maxUsesRequired': 'max_uses',
      'vouchers.errors.maxUsesRange': 'max_uses',
    };
    keys.forEach((k) => (map[field[k]] = map[field[k]] || k));

    if (this.showQuantity && !map['quantity'] && this.minQuantity > 1 && Number(r.quantity) < this.minQuantity) {
      map['quantity'] = 'vouchers.errors.quantityBelowIssued';
    }
    return map;
  }

  /** The problem to show under a field, if it should be visible yet. */
  errorFor(control: string): string | null {
    const key = this.problems[control];
    if (!key) return null;
    const c = this.form.get(control);
    return this.showAllErrors || c?.dirty || c?.touched ? key : null;
  }

  setUsage(type: 'SINGLE_USE' | 'MULTI_USE'): void {
    if (this.rulesLocked) return;
    this.form.patchValue({ usage_type: type });
    this.form.get('usage_type')?.markAsDirty();
    if (type === 'SINGLE_USE') {
      // Neither applies to a single-use voucher; clearing them keeps a stale value
      // from reaching the API if the user flips back and forth.
      this.form.patchValue({ carry_over_balance: false, max_uses: null });
    }
  }

  /** Dates before tomorrow are not selectable (the API wants a future expiry). */
  disabledDate = (d: Date): boolean => d.getTime() <= this.minDate.getTime();

  optionLabel(c: VoucherCurrency): string {
    return c.name && c.name !== c.code ? `${c.code} - ${c.name}` : c.code;
  }
}
